import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowRight, Zap, ShieldCheck, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { PayPalLogo, VenmoLogo, AppleLogo } from "@/components/PaymentIcons";

const HERO_IMG =
  "https://images.unsplash.com/photo-1522255272218-7ac5249be344?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Landing() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => {
      api.get(`/search`, { params: { q } }).then(({ data }) => setResults(data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const goToUser = (username) => navigate(`/u/${username}`);

  const submit = (e) => {
    e.preventDefault();
    const q = query.trim().replace(/^@/, "");
    if (q) goToUser(q);
  };

  return (
    <div className="App min-h-screen bg-[#050505]">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="valet" className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-[#050505]" />
        </div>

        <div className="relative z-10 mx-auto max-w-6xl px-5 py-24 sm:py-36">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-zinc-300">
              <Sparkles className="h-3.5 w-3.5 text-[#00E676]" /> No app. No cash. Just a name.
            </div>
            <h1 className="max-w-3xl font-display text-5xl font-black leading-[0.95] tracking-tighter text-white sm:text-7xl">
              Tip anyone with just their <span className="text-[#00E676]">username.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-zinc-300">
              Valets, baristas, bartenders, creators — send a tip in seconds through PayPal,
              Venmo or Apple Pay. Type a name, pick an amount, done.
            </p>

            {/* SEARCH */}
            <form onSubmit={submit} className="relative mt-12 max-w-2xl" data-testid="hero-search-form">
              <div className="flex items-center gap-3 border-b-2 border-white/20 pb-3 transition-colors focus-within:border-[#00E676]">
                <Search className="h-7 w-7 shrink-0 text-zinc-500" />
                <input
                  data-testid="hero-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Enter a username, e.g. marcusv"
                  className="w-full bg-transparent text-2xl font-medium text-white placeholder:text-zinc-600 focus:outline-none"
                />
                <button
                  type="submit"
                  data-testid="hero-search-submit"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#00E676] text-black transition-transform active:scale-90"
                >
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>

              {results.length > 0 && (
                <div
                  data-testid="hero-search-results"
                  className="absolute left-0 right-0 z-20 mt-2 overflow-hidden rounded-2xl border border-white/10 bg-[#0F0F0F] shadow-2xl"
                >
                  {results.map((r) => (
                    <button
                      type="button"
                      key={r.username}
                      data-testid={`search-result-${r.username}`}
                      onClick={() => goToUser(r.username)}
                      className="flex w-full items-center gap-4 px-5 py-3 text-left transition-colors hover:bg-white/5"
                    >
                      <div className="h-10 w-10 overflow-hidden rounded-full bg-zinc-800">
                        {r.avatar_url && (
                          <img src={r.avatar_url} alt={r.name} className="h-full w-full object-cover" />
                        )}
                      </div>
                      <div>
                        <div className="font-semibold text-white">{r.name}</div>
                        <div className="text-sm text-zinc-500">
                          @{r.username}{r.job_title ? ` · ${r.job_title}` : ""}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </form>

            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-zinc-400">
              <span className="text-xs font-bold uppercase tracking-[0.2em]">Powered by</span>
              <div className="flex items-center gap-2 text-[#008CFF]"><VenmoLogo className="h-6 w-6" /><span className="font-semibold text-white">Venmo</span></div>
              <div className="flex items-center gap-2 text-[#0079C1]"><PayPalLogo className="h-6 w-6" /><span className="font-semibold text-white">PayPal</span></div>
              <div className="flex items-center gap-2 text-white"><AppleLogo className="h-6 w-6" /><span className="font-semibold text-white">Apple Pay</span></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-6xl px-5 py-24">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">How it works</p>
        <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
          Three taps to a thank you.
        </h2>
        <div className="mt-12 grid gap-4 md:grid-cols-3">
          {[
            { icon: Search, title: "Find them", body: "Type the username on their badge, sign or card." },
            { icon: Zap, title: "Pick an amount", body: "Choose a quick tip or enter a custom amount." },
            { icon: ShieldCheck, title: "Pay direct", body: "You're sent straight to their PayPal, Venmo or Apple Pay." },
          ].map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-8 transition-transform hover:-translate-y-1"
            >
              <s.icon className="h-8 w-8 text-[#00E676]" />
              <h3 className="mt-5 font-display text-xl font-semibold">{s.title}</h3>
              <p className="mt-2 leading-relaxed text-zinc-400">{s.body}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 rounded-3xl border border-white/10 bg-gradient-to-br from-[#0F0F0F] to-[#050505] p-10 sm:p-14">
          <h2 className="max-w-lg font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Work for tips? Get your own tip link.
          </h2>
          <p className="mt-4 max-w-md leading-relaxed text-zinc-400">
            Set up your handles once and let anyone tip you with just your username.
          </p>
          <button
            data-testid="cta-get-started"
            onClick={() => navigate("/register")}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#00E676] px-7 py-3.5 font-bold text-black transition-transform active:scale-95"
          >
            Create your tip link <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 text-center text-sm text-zinc-600">
        TipNu — send a little thanks.
      </footer>
    </div>
  );
}
